import i18n, {type TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {
    countRules,
    IfConditionGroup,
    IfConditionRule,
    NO_VALUE_OPERATORS,
    operatorLabel,
    readConditionGroups,
    TODAY_VALUE
} from '../config/ifConditions';
import {CURRENT_INVITATION_FIELD_LABELS, CURRENT_SCOPE_LABELS} from '../config/continueIf';
import {nodeOutputDefinitions} from '../config/nodeOutputs';
import {readAutomationVariableReference} from './v2/variables/automationVariableText';
import NodePreview from './NodePreview';

// Explicit output labels take precedence; older and custom paths still get readable word breaks.
const outputLabels = new Map(nodeOutputDefinitions.flatMap(definition => definition.outputs)
    .filter(output => output.label).map(output => [output.name, output.label!]));

const fieldName = (path: string, t: TFunction): string => {
    const history = path.match(/^contactHistory\.(EMAIL|PUSH|ANY)(\.RESERVATION)?$/);
    if (history) {
        if (history[2]) return t(history[1] === 'EMAIL' ? 'Emails for this reservation' : history[1] === 'PUSH' ? 'Push notifications for this reservation' : 'Messages for this reservation');
        return t(history[1] === 'EMAIL' ? 'Emails from this automation' : history[1] === 'PUSH' ? 'Push notifications from this automation' : 'Messages from this automation');
    }
    const scoped = path.match(/^(currentGuest|currentReservation|currentInvitation)\.(.+)$/);
    if (scoped) {
        const field = scoped[2].replace(/\[0]/g, '');
        const label = scoped[1] === 'currentInvitation' && CURRENT_INVITATION_FIELD_LABELS[field]
            ? t(CURRENT_INVITATION_FIELD_LABELS[field]) : fieldName(field, t);
        return `${t(CURRENT_SCOPE_LABELS[scoped[1]])} · ${label}`;
    }
    const known = outputLabels.get(path);
    if (known) return t(known);

    return path.split('.').map(part => {
        const words = part.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ').toLowerCase();
        return t(words.charAt(0).toUpperCase() + words.slice(1));
    }).join(' · ');
};

/**
 * An operator label inside a sentence: English labels are Title Case, so all of it is lowercased ("does not
 * contain"); German capitalises its nouns, so only the first letter is ("weniger erhalten im Zeitraum als").
 */
const inSentence = (label: string): string => (i18n.resolvedLanguage || i18n.language || '').startsWith('de')
    ? label.charAt(0).toLocaleLowerCase('de') + label.slice(1)
    : label.toLocaleLowerCase();

const COMPARISONS: Record<string, string> = {
    equals: '=', not_equals: '≠', greater_than: '>', less_than: '<', greater_or_equal: '≥', less_or_equal: '≤'
};

/**
 * One rule in the card's words; the flow preview names unmet rules with it too. A rule's source step is
 * named only when the rules read from more than one step.
 */
export const conditionRuleDescriber = (groups: IfConditionGroup[], t: TFunction) => {
    const sources = new Set<string>();
    const collectSources = (group: IfConditionGroup) => {
        for (const rule of group.rules ?? []) {
            const references = NO_VALUE_OPERATORS.includes(rule.operator) ? [rule.field] : [rule.field, rule.value];
            for (const value of references) {
                const reference = typeof value === 'string' ? readAutomationVariableReference(value) : null;
                if (reference) sources.add(reference.nodeName);
            }
        }
        (group.groups ?? []).forEach(collectSources);
    };
    groups.forEach(collectSources);

    const describeField = (value: string): string => {
        const reference = readAutomationVariableReference(value);
        if (!reference) return fieldName(value, t);
        const label = fieldName(reference.name, t);
        return sources.size > 1 ? `${reference.nodeName} · ${label}` : label;
    };

    const describeValue = (value: unknown): string => {
        if (value === TODAY_VALUE) return t('Today');
        if (typeof value === 'boolean') return t(value ? 'True' : 'False');
        if (typeof value === 'string' && readAutomationVariableReference(value)) return describeField(value);
        if (value === undefined || value === null) return t('Not configured');
        return typeof value === 'string' ? `“${value}”` : JSON.stringify(value);
    };

    const describeRule = (rule: IfConditionRule): string => {
        if (!rule.field || !rule.operator) return t('Not configured');
        const operator = COMPARISONS[rule.operator] ?? inSentence(operatorLabel(rule.operator, t));
        // "in den letzten 10 Tagen" is dative, so these day counts have their own plural keys.
        const period = (days: unknown) => t('{{count}} days', {context: 'period', count: Number(days)});
        const value = NO_VALUE_OPERATORS.includes(rule.operator) ? ''
            : rule.operator === 'contact_not_received_in_days' ? ` ${period(rule.value)}`
            : rule.operator === 'contact_fewer_than_in_days'
                ? ` ${t('{{count}} times in {{period}}', {count: Number(rule.value?.count), period: period(rule.value?.days)})}`
            : ` ${describeValue(rule.value)}`;
        return `${describeField(rule.field)} ${operator}${value}`;
    };

    return {describeRule, sources};
};

const IfConditionSummary = ({config}: {config?: Record<string, any>}) => {
    const {t} = useTranslation();
    if (config?.conditionMode === 'AI') {
        return <NodePreview rows={[config.conditionPrompt?.trim()
            ? {label: t('AI prompt'), text: config.conditionPrompt, lines: 2}
            : {text: t('No condition set')} ]}/>;
    }

    const groups = readConditionGroups(config);
    const count = groups.reduce((total, group) => total + countRules(group), 0);
    if (!count) return <NodePreview rows={[{text: t('No condition set')}]}/>;

    const {describeRule, sources} = conditionRuleDescriber(groups, t);

    const describeGroup = (group: IfConditionGroup): string => {
        const members = [
            ...(group.rules ?? []).map(describeRule),
            ...(group.groups ?? []).map(describeGroup)
        ].filter(Boolean);
        const expression = members.join(` ${t(group.operator)} `);
        return members.length > 1 ? `(${expression})` : expression;
    };

    const expression = groups.map(describeGroup).filter(Boolean).join(` ${t('OR')} `);
    const source = sources.size === 1 ? Array.from(sources)[0] : '';

    return <NodePreview rows={[
        {text: expression, lines: 2},
        ...(count > 1
            ? [{text: [t('{{count}} conditions', {count}), source].filter(Boolean).join(' · ')}]
            : source ? [{label: t('From'), text: source}] : [])
    ]}/>;
};

export default IfConditionSummary;
